import { useEffect, useMemo } from "react";
import { create } from "zustand";
import { useNavigate } from "@tanstack/react-router";
import axios from "axios";
import {
  defaultSelection,
  grantedCount,
  toScopeStrings,
  type ScopeSelection,
} from "./scopes";
import { useQuota, useSandboxesStore } from "./store";
import { useDefaultCountry } from "./country";
import { EXPIRY_PERIODS, type Country, type ExpiryPeriod } from "./types";

const NAME_RULE = /^[a-zA-Z0-9.-]{1,255}$/;

export const SHAPE_HINT =
  "Letters, numbers, dots and hyphens. Spaces are converted to hyphens.";
const SHAPE_ERROR = "Use letters, numbers, dots or hyphens, up to 255 characters.";
// Two problems share 409 on create, so the code decides which one this is.
const DUPLICATE_NAME = "pfdn_sandbox_duplicate_name";

interface FormState {
  name: string;
  country: Country;
  expiry: ExpiryPeriod;
  selection: ScopeSelection | null;
  busy: boolean;
  serverError: string | null;
  nameConflict: string | null;
  setName: (name: string) => void;
  setCountry: (country: Country) => void;
  setExpiry: (expiry: ExpiryPeriod) => void;
  setLevel: (groupKey: string, levelKey: string) => void;
  setSelection: (selection: ScopeSelection | null) => void;
  setBusy: (busy: boolean) => void;
  setServerError: (message: string | null) => void;
  setNameConflict: (message: string | null) => void;
  reset: () => void;
}

const initial = {
  name: "",
  country: "ae" as Country,
  expiry: EXPIRY_PERIODS[0],
  selection: null,
  busy: false,
  serverError: null,
  nameConflict: null,
};

const useFormStore = create<FormState>((set) => ({
  ...initial,
  setName: (name) => set({ name, serverError: null, nameConflict: null }),
  setCountry: (country) => set({ country }),
  setExpiry: (expiry) => set({ expiry }),
  setLevel: (groupKey, levelKey) =>
    set((s) => ({ selection: { ...(s.selection ?? {}), [groupKey]: levelKey } })),
  setSelection: (selection) => set({ selection }),
  setBusy: (busy) => set({ busy }),
  setServerError: (serverError) => set({ serverError }),
  setNameConflict: (nameConflict) => set({ nameConflict }),
  reset: () => set(initial),
}));

export function useSandboxForm() {
  const navigate = useNavigate();
  const form = useFormStore();
  const defaultCountry = useDefaultCountry();

  const createSandbox = useSandboxesStore((s) => s.create);
  const load = useSandboxesStore((s) => s.load);
  const loadScopeGroups = useSandboxesStore((s) => s.loadScopeGroups);
  const { active, maxActive, atQuota, canCreate } = useQuota();
  const scopeGroups = useSandboxesStore((s) => s.scopeGroups);
  const scopeStatus = useSandboxesStore((s) => s.scopeStatus);

  const { reset, setSelection, setCountry } = form;

  useEffect(() => {
    reset();
    void load();
    void loadScopeGroups();
  }, [reset, load, loadScopeGroups]);

  useEffect(() => {
    if (defaultCountry) setCountry(defaultCountry);
  }, [defaultCountry, setCountry]);

  useEffect(() => {
    if (scopeGroups && !form.selection) setSelection(defaultSelection(scopeGroups));
  }, [scopeGroups, form.selection, setSelection]);

  const { granted, fullAccess } = useMemo(
    () =>
      scopeGroups && form.selection
        ? grantedCount(scopeGroups, form.selection)
        : { granted: 0, fullAccess: 0 },
    [scopeGroups, form.selection],
  );

  const shapeError = form.name.length > 0 && !NAME_RULE.test(form.name);
  const nameError = shapeError ? SHAPE_ERROR : (form.nameConflict ?? undefined);

  return {
    ...form,
    scopeGroups,
    scopeStatus,
    groupCount: scopeGroups?.length ?? 0,
    granted,
    fullAccess,
    nameError,
    active,
    maxActive,
    atQuota,
    valid: canCreate && NAME_RULE.test(form.name) && granted > 0,
    onNameChange: (value: string) => form.setName(value.replace(/\s+/g, "-")),
    onResetScopes: () =>
      form.setSelection(scopeGroups ? defaultSelection(scopeGroups) : null),
    onRetryScopes: () => void loadScopeGroups(),
    onCancel: () => void navigate({ to: "/sandboxes" }),
    onSubmit: async () => {
      form.setBusy(true);
      form.setServerError(null);
      try {
        await createSandbox({
          name: form.name,
          country: form.country,
          expiryPeriodInDays: form.expiry,
          scopes: toScopeStrings(scopeGroups ?? [], form.selection ?? {}),
        });
        await navigate({ to: "/sandboxes" });
      } catch (error) {
        const problem = axios.isAxiosError(error)
          ? (error.response?.data as { code?: string; detail?: string })
          : undefined;
        if (problem?.code === DUPLICATE_NAME) {
          form.setNameConflict(
            problem.detail ?? "A sandbox with this name already exists.",
          );
        } else {
          form.setServerError(
            problem?.detail ?? "Could not create the sandbox. Try again.",
          );
        }
      } finally {
        form.setBusy(false);
      }
    },
  };
}
